import type { ReactNode } from 'react';
import { ConfigProvider, Flex, theme } from 'antd';
import Portal from '@/components/Portal';
import usePrefetchOnHover from '@/hooks/usePrefetchOnHover';
import { useModalState, useBodyScrollLock, useModalDataState } from '@/hooks/modals';
import type { DownloadConfig } from '@/types/modals';
import ModalHeaderEcho from './ModalHeaderEcho';
import ModalBodyEcho from './ModalBodyEcho';
import type { DateRange, ShowFilter } from '@/types';

type ModalProps = {
  title: string;
  children: (dateRange: DateRange, selectedShow: ShowFilter) => ReactNode;
  hashProp: string;
  showDateRangeFilter?: boolean;
  showShowsFilter?: boolean;
  renderTrigger?: (openModal: () => void, handlePrefetch: () => void) => ReactNode;
  onPrefetch?: () => void;
  onClose?: () => void;
  onOpen?: () => void;
  downloadConfig?: DownloadConfig;
  onDateRangeOpen?: () => void;
  onDateRangeApply?: (dateRange: DateRange) => void;
  onDateRangePresetSelect?: (preset: string) => void;
  onShowFilterOpen?: () => void;
  onShowFilterApply?: (selectedShow: ShowFilter) => void;
};

/**
 * This component is used to render a modal.

 * @param title - The title of the modal.
 * @param children - The children of the modal.
 * @param hashProp - The hash prop of the modal.
 * @param showShowsFilter - Whether to show the shows filter.
 * @param showDateRangeFilter - Whether to show the date range filter.
 * @param renderTrigger - The trigger to render the modal.
 * @param onPrefetch - The function to prefetch the modal.
 * @param onClose - The function to close the modal.
 * @param onDateRangeOpen - The function to when the date range filter is opened.
 * @param onDateRangePresetSelect - The function to when a date range preset is selected.
 * @param onDateRangeApply - The function to when the date range filter is applied.
 * @param downloadConfig - The download config for the modal.
 * @param onShowFilterOpen - The function to when the shows filter is opened.
 * @param onShowFilterApply - The function to when the shows filter is applied.
 * @returns The modal component.
 */
const ModalEcho = ({
  title,
  children,
  hashProp,
  showShowsFilter,
  showDateRangeFilter,
  renderTrigger,
  onPrefetch,
  onClose,
  onOpen,
  onDateRangeOpen,
  onDateRangePresetSelect,
  onDateRangeApply,
  downloadConfig,
  onShowFilterOpen,
  onShowFilterApply,
}: ModalProps) => {
  const { token } = theme.useToken();
  const { isModalOpen, openModal, closeModal } = useModalState(hashProp, onOpen);
  const { modalDateRange, handleDateRangeChange, modalSelectedShow, handleSelectedShowChange } =
    useModalDataState(isModalOpen);
  useBodyScrollLock(isModalOpen);

  const handlePrefetch = usePrefetchOnHover(onPrefetch ?? (() => {}), !isModalOpen);

  const handleDateRangeApply = (dateRange: DateRange) => {
    onDateRangeApply?.(dateRange);
    handleDateRangeChange(dateRange);
  };

  const handleSelectedShowApply = (selectedShow: ShowFilter) => {
    handleSelectedShowChange(selectedShow);
    onShowFilterApply?.(selectedShow);
  };

  const handleClose = () => {
    onClose?.();
    closeModal();
  };

  // If we have a renderTrigger and modal is closed, render only the trigger
  if (!isModalOpen) {
    if (renderTrigger) {
      return renderTrigger(openModal, handlePrefetch);
    }
    return null;
  }

  return (
    <Portal>
      <ConfigProvider theme={{ token: { zIndexPopupBase: token.zIndexPopupBase + 200 } }}>
        <Flex
          vertical
          data-testid="modal-overlay"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: token.zIndexPopupBase + 100,
            background: token.colorBgContainer,
            boxShadow: token.boxShadowSecondary,
            overflow: 'auto',
          }}
        >
          <ModalHeaderEcho
            title={title}
            closeModal={handleClose}
            downloadConfig={downloadConfig}
            modalDateRange={modalDateRange}
            modalSelectedShow={modalSelectedShow}
          />

          <ModalBodyEcho
            modalSelectedShow={modalSelectedShow}
            onShowFilterOpen={onShowFilterOpen}
            onShowFilterApply={handleSelectedShowApply}
            showShowsFilter={showShowsFilter!}
            showDateRangeFilter={showDateRangeFilter!}
            modalDateRange={modalDateRange}
            handleDateRangeApply={handleDateRangeApply}
            onDateRangeOpen={onDateRangeOpen}
            onDateRangePresetSelect={onDateRangePresetSelect}
          >
            {children}
          </ModalBodyEcho>
        </Flex>
      </ConfigProvider>
    </Portal>
  );
};

export default ModalEcho;
